import React, {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  type Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from 'react';
import { type Details, type MapMarkerProps, type Region } from 'react-native-maps';
import { type BorderProps, type LayoutProps, type SpacingProps } from '@shopify/restyle';
import {
  AdvancedMarker,
  APIProvider,
  Map as GoogleMap,
  GoogleMapsContext,
  ControlPosition,
  latLngEquals,
  MapControl,
  Pin,
  useMap,
  type MapEvent,
} from '@vis.gl/react-google-maps';
import { env } from 'app/lib/env';
import type { Theme } from 'app/shopify';
import { createTheme as createDSTheme } from 'app/shopify';
import { Pressable, StyleSheet } from 'react-native';
import { IconFocus2 } from '@tabler/icons-react-native';
import { Column } from '../box';
const theme = createDSTheme();
const MAX_LATITUDE_DELTA = 170;
const MAX_LONGITUDE_DELTA = 360;
const SELECTED_MARKER_Z_INDEX = 1_000;
const REGION_ABSOLUTE_EPSILON = 0.0000001;
const REGION_RELATIVE_EPSILON = 0.000001;

type RestyleProps = BorderProps<Theme> & LayoutProps<Theme> & SpacingProps<Theme>;
interface LPCMapMarkerProps {
  coordinate: MapMarkerProps['coordinate'];
  children?: ReactNode;
  onPress?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  selected?: boolean;
}
interface MapDimOverlayProps {
  fillColor?: string;
  fillOpacity?: number;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

function valuesAreEquivalent(first: number, second: number, scale: number) {
  return Math.abs(first - second) <= Math.max(REGION_ABSOLUTE_EPSILON, scale * REGION_RELATIVE_EPSILON);
}

function longitudeDistance(first: number, second: number) {
  const directDistance = Math.abs(first - second) % MAX_LONGITUDE_DELTA;
  return Math.min(directDistance, MAX_LONGITUDE_DELTA - directDistance);
}

function regionsAreEquivalent(first: Region | undefined, second: Region | undefined) {
  if (!first || !second) return first === second;

  const latitudeScale = Math.max(Math.abs(first.latitudeDelta), Math.abs(second.latitudeDelta));
  const longitudeScale = Math.max(Math.abs(first.longitudeDelta), Math.abs(second.longitudeDelta));

  return (
    valuesAreEquivalent(first.latitude, second.latitude, latitudeScale) &&
    longitudeDistance(first.longitude, second.longitude) <=
      Math.max(REGION_ABSOLUTE_EPSILON, longitudeScale * REGION_RELATIVE_EPSILON) &&
    valuesAreEquivalent(first.latitudeDelta, second.latitudeDelta, latitudeScale) &&
    valuesAreEquivalent(first.longitudeDelta, second.longitudeDelta, longitudeScale)
  );
}

function getZoomFromRegion(region: Region, viewportWidth: number) {
  return Math.max(
    0,
    Math.min(22, Math.log2((360 * Math.max(viewportWidth, 256)) / (256 * Math.max(region.longitudeDelta, 0.001)))),
  );
}

function clampOpacity(opacity: number) {
  return Math.max(0, Math.min(1, opacity));
}

function resolveDimOverlayOptions(fillColor: string, fillOpacity?: number) {
  const hexWithAlpha = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(fillColor);
  const rgbHex = hexWithAlpha?.[1];
  const alphaHex = hexWithAlpha?.[2];

  return {
    fillColor: rgbHex ? `#${rgbHex}` : fillColor,
    fillOpacity: clampOpacity(fillOpacity ?? (alphaHex ? Number.parseInt(alphaHex, 16) / 255 : 1)),
  };
}

function getLongitudeDeltaFromZoom(zoom: number, viewportWidth: number) {
  return Math.min(
    MAX_LONGITUDE_DELTA,
    (MAX_LONGITUDE_DELTA * Math.max(viewportWidth, 256)) / (256 * 2 ** Math.max(0, Math.min(22, zoom))),
  );
}

function normalizeLongitudeDelta(east: number, west: number) {
  const directDelta = east - west;
  if (!Number.isFinite(directDelta)) return undefined;

  const wrappedDelta = ((directDelta % MAX_LONGITUDE_DELTA) + MAX_LONGITUDE_DELTA) % MAX_LONGITUDE_DELTA;
  const absoluteDelta = Math.abs(directDelta);
  const longitudeDelta = wrappedDelta > 0 ? wrappedDelta : absoluteDelta;

  return longitudeDelta > 0 && longitudeDelta < MAX_LONGITUDE_DELTA ? longitudeDelta : undefined;
}

function getRegionFromBounds(
  center: { lat: number; lng: number },
  bounds: { north: number; south: number; east: number; west: number },
  zoom?: number,
  viewportWidth = 256,
) {
  const latitudeDeltaFromBounds = Math.abs(bounds.north - bounds.south);
  const longitudeDelta =
    normalizeLongitudeDelta(bounds.east, bounds.west) ??
    (zoom === undefined ? MAX_LONGITUDE_DELTA : getLongitudeDeltaFromZoom(zoom, viewportWidth));

  return {
    latitude: center.lat,
    longitude: center.lng,
    latitudeDelta:
      latitudeDeltaFromBounds > 0 && latitudeDeltaFromBounds <= MAX_LATITUDE_DELTA
        ? latitudeDeltaFromBounds
        : Math.min(MAX_LATITUDE_DELTA, longitudeDelta),
    longitudeDelta: Math.max(0.001, Math.min(MAX_LONGITUDE_DELTA, longitudeDelta)),
  };
}

function getRegionFromMapEvent(event: MapEvent) {
  const bounds = event.map.getBounds();
  if (!bounds) return undefined;
  const zoom = event.map.getZoom();
  const viewportWidth = event.map.getDiv().clientWidth;
  return getRegionFromBounds(
    { lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() },
    {
      north: bounds.getNorthEast().lat(),
      south: bounds.getSouthWest().lat(),
      east: bounds.getNorthEast().lng(),
      west: bounds.getSouthWest().lng(),
    },
    zoom,
    viewportWidth,
  );
}

function CameraController({
  controlled,
  lastObservedRegionRef,
  programmaticCameraUpdatePendingRef,
  region,
  viewportWidth,
}: {
  controlled: boolean;
  lastObservedRegionRef: { current: Region | undefined };
  programmaticCameraUpdatePendingRef: { current: boolean };
  region: Region;
  viewportWidth: number;
}) {
  const map = useContext(GoogleMapsContext)?.map;
  const previousTarget = useRef({ controlled, region, viewportWidth });

  useEffect(() => {
    const previous = previousTarget.current;
    const becameControlled = controlled && !previous.controlled;
    const regionChanged = !regionsAreEquivalent(previous.region, region);
    const viewportWidthChanged = previous.viewportWidth !== viewportWidth;

    if (!controlled || (!becameControlled && !regionChanged && !viewportWidthChanged)) {
      previousTarget.current = { controlled, region, viewportWidth };
      return;
    }
    if (!viewportWidthChanged && regionsAreEquivalent(lastObservedRegionRef.current, region)) {
      previousTarget.current = { controlled, region, viewportWidth };
      return;
    }
    // Keep the previous target until the map exists so a region update that
    // arrives while Google Maps is loading is not lost.
    if (!map) return;

    previousTarget.current = { controlled, region, viewportWidth };
    programmaticCameraUpdatePendingRef.current = true;
    map.moveCamera({
      center: { lat: region.latitude, lng: region.longitude },
      zoom: getZoomFromRegion(region, viewportWidth),
    });
  }, [controlled, lastObservedRegionRef, map, programmaticCameraUpdatePendingRef, region, viewportWidth]);

  return null;
}

export function Map({
  children,
  region,
  rotateEnabled = true,
  recenterButton,
  onRegionChange,
  onRegionChangeComplete,
  controlledCamera,
  initialBounds,
  initialBoundsPadding = 0,
  width,
  ...rest
}: {
  children: React.ReactElement | React.ReactElement[];
  region: {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
  };
  rotateEnabled?: boolean;
  recenterButton?: boolean | Region;
  onRegionChange?: (region: Region) => void;
  onRegionChangeComplete?: (region: Region, details: Details) => void;
  controlledCamera?: boolean;
  initialBounds?: MapBounds;
  initialBoundsPadding?: number;
} & RestyleProps) {
  const usesImperativeCamera = controlledCamera === true;
  const viewportWidth = typeof width === 'number' ? width : 256;
  const controlledZoom = getZoomFromRegion(region, viewportWidth);
  const controlledCenter = { lat: region.latitude, lng: region.longitude };
  const [initialCamera] = useReducer(
    (current: {
      bounds: MapBounds | undefined;
      boundsPadding: number;
      center: { lat: number; lng: number };
      zoom: number;
    }) => current,
    undefined,
    () => ({
      bounds: initialBounds,
      boundsPadding: initialBoundsPadding,
      center: controlledCenter,
      zoom: controlledZoom,
    }),
  );
  const initialCameraProps = initialCamera.bounds
    ? {
        defaultBounds: {
          ...initialCamera.bounds,
          padding: initialCamera.boundsPadding,
        },
      }
    : {
        defaultCenter: initialCamera.center,
        defaultZoom: initialCamera.zoom,
      };
  const legacyControlledCameraProps =
    controlledCamera === undefined
      ? {
          center: controlledCenter,
          defaultCenter: initialCamera.center,
          defaultZoom: initialCamera.zoom,
          zoom: controlledZoom,
        }
      : initialCameraProps;
  const gesturePending = useRef(false);
  const lastChangedRegion = useRef<Region | undefined>(undefined);
  const lastCompletedRegion = useRef<Region | undefined>(undefined);
  const lastObservedRegion = useRef<Region | undefined>(usesImperativeCamera ? region : undefined);
  const programmaticCameraUpdatePending = useRef(controlledCamera !== false);

  useEffect(() => {
    if (controlledCamera === undefined) programmaticCameraUpdatePending.current = true;
  }, [controlledCamera, controlledZoom, region.latitude, region.longitude]);

  const shouldAcceptCameraRegion = (nextRegion: Region) => {
    const nextZoom = getZoomFromRegion(nextRegion, viewportWidth);
    return !(controlledZoom > 4 && nextZoom < 2);
  };
  const handleDragStart = () => {
    gesturePending.current = true;
    programmaticCameraUpdatePending.current = false;
  };
  const handleDrag = (event: MapEvent) => {
    gesturePending.current = true;
    programmaticCameraUpdatePending.current = false;
    const nextRegion = getRegionFromMapEvent(event);
    if (!nextRegion || !shouldAcceptCameraRegion(nextRegion)) return;

    lastObservedRegion.current = { ...nextRegion };
    if (regionsAreEquivalent(lastChangedRegion.current, nextRegion)) return;
    lastChangedRegion.current = { ...nextRegion };
    onRegionChange?.(nextRegion);
  };
  const handleIdle = (event: MapEvent) => {
    const nextRegion = getRegionFromMapEvent(event);
    const isGesture = gesturePending.current || !programmaticCameraUpdatePending.current;
    gesturePending.current = false;
    programmaticCameraUpdatePending.current = false;

    if (!nextRegion || !shouldAcceptCameraRegion(nextRegion)) return;
    lastObservedRegion.current = { ...nextRegion };
    if (regionsAreEquivalent(lastCompletedRegion.current, nextRegion)) return;
    lastCompletedRegion.current = { ...nextRegion };
    onRegionChangeComplete?.(nextRegion, { isGesture });
  };

  return (
    <Column {...rest} width={width}>
      <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY_WEB}>
        <GoogleMap
          {...legacyControlledCameraProps}
          mapId="PartageClubWeb"
          rotateControl={rotateEnabled} // @TODO also prevent rotation gesture!
          gestureHandling="greedy"
          renderingType="VECTOR"
          fullscreenControl={false}
          streetViewControl={false}
          zoomControl={false}
          scaleControl={false}
          mapTypeControl={false}
          cameraControl={false}
          onDrag={handleDrag}
          onDragstart={handleDragStart}
          onIdle={handleIdle}
        >
          <CameraController
            controlled={usesImperativeCamera}
            lastObservedRegionRef={lastObservedRegion}
            programmaticCameraUpdatePendingRef={programmaticCameraUpdatePending}
            region={region}
            viewportWidth={viewportWidth}
          />
          {children}
          {recenterButton ? (
            <RecenterButton region={typeof recenterButton === 'object' ? recenterButton : region} />
          ) : null}
        </GoogleMap>
      </APIProvider>
    </Column>
  );
}

function RecenterButton({ region }: { region: Region }) {
  const map = useMap();
  return (
    <MapControl position={ControlPosition.RIGHT_BOTTOM}>
      <Pressable
        accessibilityLabel="Recenter map"
        accessibilityRole="button"
        onPress={() => {
          map?.fitBounds({
            east: region.longitude + region.longitudeDelta,
            north: region.latitude + region.latitudeDelta,
            south: region.latitude - region.latitudeDelta,
            west: region.longitude - region.longitudeDelta,
          });
        }}
        style={[
          styles.recenterButton,
          { backgroundColor: theme.colors['surface.default'], shadowColor: theme.colors['text.primary'] },
        ]}
      >
        <IconFocus2 color={theme.colors['icon.primary']} size={24} />
      </Pressable>
    </MapControl>
  );
}

const styles = StyleSheet.create({
  recenterButton: {
    alignItems: 'center',
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    margin: 16,
    shadowOffset: { height: 1, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    width: 48,
  },
});

Map.Marker = function Marker({ coordinate, children, onPress, onHoverIn, onHoverOut, selected }: LPCMapMarkerProps) {
  // Google Maps rejects undefined, null, NaN, and infinite coordinates when assigning
  // AdvancedMarker.position. API responses can briefly contain incomplete coordinates
  // while listing/address data is loading, so skip those markers until they are valid.
  if (!Number.isFinite(coordinate.latitude) || !Number.isFinite(coordinate.longitude)) {
    return null;
  }

  const markerEvents = {
    onClick: onPress,
    onMouseEnter: onHoverIn,
    onMouseLeave: onHoverOut,
  } as Partial<ComponentProps<typeof AdvancedMarker>>;

  return (
    <AdvancedMarker
      {...markerEvents}
      position={{ lat: coordinate.latitude, lng: coordinate.longitude }}
      zIndex={selected ? SELECTED_MARKER_Z_INDEX : undefined}
    >
      {children ?? (
        <Pin
          background={theme.colors['icon.primary']}
          borderColor={theme.colors['icon.primary']}
          glyphColor={theme.colors['icon.primary']}
        />
      )}
    </AdvancedMarker>
  );
};

Map.Circle = function MapCircle({
  center,
  fillColor,
  fillOpacity = 1,
  radius,
  strokeColor,
  strokeWidth = 1,
}: Pick<CircleProps, 'fillColor' | 'fillOpacity' | 'radius' | 'strokeColor'> & {
  center: { latitude: number; longitude: number };
  strokeWidth?: number;
}) {
  return (
    <Circle
      center={{ lat: center.latitude, lng: center.longitude }}
      fillColor={fillColor ?? theme.colors['surface.overlay']}
      fillOpacity={fillOpacity}
      radius={radius}
      strokeColor={strokeColor ?? theme.colors['border.strong']}
      strokeWeight={strokeWidth}
    />
  );
};

Map.DimOverlay = function DimOverlay({ fillColor, fillOpacity }: MapDimOverlayProps) {
  const overlay = resolveDimOverlayOptions(fillColor ?? theme.colors['surface.overlay'], fillOpacity);

  return (
    <Rectangle
      bounds={{ north: 85, south: -85, east: 180, west: -180 }}
      clickable={false}
      fillColor={overlay.fillColor}
      fillOpacity={overlay.fillOpacity}
      strokeOpacity={0}
    />
  );
};

function Rectangle(options: google.maps.RectangleOptions) {
  const rectangle = useRef<google.maps.Rectangle | null>(null);
  const map = useContext(GoogleMapsContext)?.map;

  useEffect(() => {
    if (!map) {
      if (map === undefined) console.error('<Rectangle> has to be inside a Map component.');
      return;
    }

    const nextRectangle = new google.maps.Rectangle();
    rectangle.current = nextRectangle;
    nextRectangle.setMap(map);

    return () => {
      nextRectangle.setMap(null);
      if (rectangle.current === nextRectangle) rectangle.current = null;
    };
  }, [map]);

  useEffect(() => {
    rectangle.current?.setOptions(options);
  }, [options]);

  return null;
}

// Below is from: https://github.com/visgl/react-google-maps/blob/main/examples/geometry/src/components/circle.tsx

interface CircleEventProps {
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDrag?: (e: google.maps.MapMouseEvent) => void;
  onDragStart?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
  onMouseOver?: (e: google.maps.MapMouseEvent) => void;
  onMouseOut?: (e: google.maps.MapMouseEvent) => void;
  onRadiusChanged?: (r: ReturnType<google.maps.Circle['getRadius']>) => void;
  onCenterChanged?: (p: ReturnType<google.maps.Circle['getCenter']>) => void;
}

export type CircleProps = google.maps.CircleOptions & CircleEventProps;

export type CircleRef = Ref<google.maps.Circle | null>;

function useCircle(props: CircleProps) {
  const {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    onRadiusChanged,
    onCenterChanged,
    radius,
    center,
    ...circleOptions
  } = props;
  // This is here to avoid triggering the useEffect below when the callbacks change (which happen if the user didn't memoize them)
  const callbacks = useRef<Record<string, (e: unknown) => void>>({});
  useEffect(() => {
    Object.assign(callbacks.current, {
      onClick,
      onDrag,
      onDragStart,
      onDragEnd,
      onMouseOver,
      onMouseOut,
      onRadiusChanged,
      onCenterChanged,
    });
  }, [onCenterChanged, onClick, onDrag, onDragEnd, onDragStart, onMouseOut, onMouseOver, onRadiusChanged]);

  const [circle] = useReducer(
    (current: google.maps.Circle) => current,
    undefined,
    () => new google.maps.Circle(),
  );
  // update circleOptions (note the dependencies aren't properly checked
  // here, we just assume that setOptions is smart enough to not waste a
  // lot of time updating values that didn't change)
  useEffect(() => {
    circle.setOptions(circleOptions);
  }, [circle, circleOptions]);

  useEffect(() => {
    if (!center) return;
    if (!latLngEquals(center, circle.getCenter())) circle.setCenter(center);
  }, [center, circle]);

  useEffect(() => {
    if (radius === undefined || radius === null) return;
    if (radius !== circle.getRadius()) circle.setRadius(radius);
  }, [circle, radius]);

  const map = useContext(GoogleMapsContext)?.map;

  // create circle instance and add to the map once the map is available
  useEffect(() => {
    if (!map) {
      if (map === undefined) console.error('<Circle> has to be inside a Map component.');

      return;
    }

    circle.setMap(map);

    return () => {
      circle.setMap(null);
    };
  }, [circle, map]);

  // attach and re-attach event-handlers when any of the properties change
  useEffect(() => {
    // Add event listeners
    const gme = google.maps.event;
    const clickListener = gme.addListener(circle, 'click', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onClick?.(event),
    );
    const dragListener = gme.addListener(circle, 'drag', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onDrag?.(event),
    );
    const dragStartListener = gme.addListener(circle, 'dragstart', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onDragStart?.(event),
    );
    const dragEndListener = gme.addListener(circle, 'dragend', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onDragEnd?.(event),
    );
    const mouseOverListener = gme.addListener(circle, 'mouseover', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onMouseOver?.(event),
    );
    const mouseOutListener = gme.addListener(circle, 'mouseout', (event: google.maps.MapMouseEvent) =>
      callbacks.current.onMouseOut?.(event),
    );
    const radiusListener = gme.addListener(circle, 'radius_changed', () => {
      callbacks.current.onRadiusChanged?.(circle.getRadius());
    });
    const centerListener = gme.addListener(circle, 'center_changed', () => {
      callbacks.current.onCenterChanged?.(circle.getCenter());
    });

    return () => {
      clickListener.remove();
      dragListener.remove();
      dragStartListener.remove();
      dragEndListener.remove();
      mouseOverListener.remove();
      mouseOutListener.remove();
      radiusListener.remove();
      centerListener.remove();
    };
  }, [circle]);

  return circle;
}

/**
 * Component to render a circle on a map
 */
// eslint-disable-next-line react/display-name
const Circle = forwardRef((props: CircleProps, ref: CircleRef) => {
  const circle = useCircle(props);
  useImperativeHandle(ref, () => circle);
  return null;
});
